import { useEffect, useState } from "react"
import { Menu } from "@headlessui/react"

import { useAuth } from "contexts/auth-context"

const baseUrl = () => (process.env.REACT_APP_INVENTORY_API_URL || "").replace(/\/$/, "")

// DO = dokumen picking; sejak print dibuka pra-approve, cek dulu stok HO masih
// cukup atau tidak. Kalau kurang → tampilkan daftar & blokir (harus koreksi dulu).
// Backend tetap hard-block (assertDoStockAvailable) — ini hanya supaya alert rapi.
const checkDoStock = async (poId) => {
  try {
    const res = await fetch(`${baseUrl()}/purchase-orders/${poId}/do-stock-check`)
    if (!res.ok) return true // gagal cek (mis. jaringan) → biarkan backend yang memutuskan
    const data = await res.json()
    if (data?.ok) return true

    const detail = (data?.shortages || [])
      .map((s) => `• ${s.name} — butuh ${s.qty_ordered}, stok HO ${s.available}`)
      .join("\n")
    window.alert(`Stok HO tidak cukup untuk print DO.\nKoreksi stok dulu:\n\n${detail}`)
    return false
  } catch {
    return true
  }
}

// Return true bila dokumen benar-benar dibuka (lolos cek stok), false bila dibatalkan.
const openExport = async (poId, item, viewerOutletId) => {
  if (!poId) return false
  if (item.checkStock && !(await checkDoStock(poId))) return false
  // 200726: outlet aktif user dikirim ke print — bila = outlet tujuan PO, dokumen
  // DO berjudul "Barang Masuk" (bukti terima di sisi outlet penerima).
  const qs = viewerOutletId ? `?viewer_outlet_id=${encodeURIComponent(viewerOutletId)}` : ""
  window.open(`${baseUrl()}/purchase-orders/${poId}${item.path}${qs}`, "_blank")
  return true
}

const items = [
  // 210726 (permintaan user): varian "(Lusin)" dihapus dari menu — Faktur & DO PDF
  // sudah mengikuti UOM PO (PCS/LUSIN/PASANG: qty ÷ faktor, harga × faktor, kolom
  // Satuan ikut). Route lama /print/faktur-lusin & /print/delivery-order-lusin
  // tetap ada di backend (kompatibilitas).
  {
    label: "Faktur PDF",
    description: "Sesuai satuan PO",
    path: "/print/faktur",
  },
  {
    label: "Print Faktur Detail",
    description: "Rinci per product unit",
    path: "/print/faktur-detail",
  },
  // ATALLA-434 (rev): Faktur Gudang Global di-hide dari menu (route backend
  // /print/faktur-gudang-global tetap ada), diganti DO Gudang Global di bawah.
  // {
  //   label: "Faktur Gudang Global",
  //   description: "Semua item + gudang",
  //   path: "/print/faktur-gudang-global",
  // },
  {
    label: "Print Surat Jalan",
    description: "Semua item + gudang",
    path: "/print/delivery-order-gudang-global",
    checkStock: true,
  },
  {
    label: "DO PDF",
    description: "Sesuai satuan PO",
    path: "/print/delivery-order",
    checkStock: true,
    // 240726 (permintaan user): DO boleh dicetak PRA-APPROVE (DRAFT/SUBMITTED) —
    // dokumen picking gudang; cek stok HO tetap jalan. Faktur & Surat Jalan
    // tetap pasca-approve.
    draftAllowed: true,
  },
  // 300726 (permintaan user): tabel menerus semua item (ala Surat Jalan),
  // header & footer ala DO per gudang. Perilaku cetak = DO (boleh pra-approve).
  {
    label: "DO Gudang Global",
    description: "Semua item, format DO",
    path: "/print/do-gudang-global",
    checkStock: true,
    draftAllowed: true,
  },
  {
    label: "Faktur PRN",
    description: "Format lama",
    path: "/export-legacy/faktur",
  },
  {
    label: "DO PRN",
    description: "Format lama",
    path: "/export-legacy/delivery-order",
    checkStock: true,
    draftAllowed: true,
  },
]

// 270726 (permintaan user): Faktur & Surat Jalan boleh dicetak sejak PO SUBMITTED
// (sebelumnya harus APPROVED). Hanya DRAFT yang masih dibatasi ke item DO saja.
const FAKTUR_SJ_STATUSES = ["SUBMITTED", "APPROVED", "PARTIAL_RECEIVED", "RECEIVED", "CLOSED"]

export default function PurchaseExportActions({ poId, poStatus = "", doPrinted = false, align = "right", compact = false }) {
  const { currentBranch } = useAuth()
  const viewerOutletId = currentBranch?.id ? String(currentBranch.id) : ""

  // 240726 (permintaan user): setelah DO dicetak, tombol Faktur/SJ langsung aktif TANPA
  // refresh. Cetak DO lewat window.open (tab baru) → tak ada callback dari server, jadi
  // begitu item DO berhasil dibuka (lolos cek stok) kita tandai lokal DO sudah dicetak.
  // effectiveDoPrinted = prop dari po.do_no ATAU tanda lokal. Reset saat ganti PO.
  const [locallyPrinted, setLocallyPrinted] = useState(false)
  useEffect(() => { setLocallyPrinted(false) }, [poId])

  if (!poId) return null

  const effectiveDoPrinted = doPrinted || locallyPrinted

  // 240726 (revisi permintaan user): DO (draftAllowed) boleh dicetak status apa saja;
  // saat DRAFT HANYA tampilkan item DO. Faktur & Surat Jalan butuh (a) SUBMITTED ke atas
  // dan (b) DO SUDAH dicetak (effectiveDoPrinted) — kalau belum, item-nya di-DISABLE + tooltip.
  // Tanpa poStatus (pemanggil lama) → perilaku longgar (tampilkan semua).
  const canPrintFakturSj = poStatus === "" || FAKTUR_SJ_STATUSES.includes(String(poStatus).toUpperCase())
  const visibleItems = canPrintFakturSj ? items : items.filter((item) => item.draftAllowed)
  const isFakturSjLocked = (item) => !item.draftAllowed && !effectiveDoPrinted && poStatus !== ""

  // Cetak dokumen; bila yang dicetak DO (draftAllowed) & berhasil dibuka → aktifkan Faktur/SJ.
  const handleOpen = async (item) => {
    const opened = await openExport(poId, item, viewerOutletId)
    if (opened && item.draftAllowed) setLocallyPrinted(true)
  }

  return (
    <Menu as="div" className="relative inline-block text-left">
      <Menu.Button
        className={`inline-flex items-center gap-2 border border-neutral-200 bg-white text-neutral-800 shadow-sm transition hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-neutral-200 ${
          compact ? "px-3 py-2 text-[11px] rounded-lg" : "px-4 py-3 text-xs font-semibold rounded-xl"
        }`}
      >
        <span>Export</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-3.5 h-3.5 text-neutral-500"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </Menu.Button>

      <Menu.Items
        className={`absolute z-30 mt-2 w-56 overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-xl ring-1 ring-black/5 focus:outline-none ${
          align === "left" ? "left-0 origin-top-left" : "right-0 origin-top-right"
        }`}
      >
        <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-neutral-400 bg-neutral-50">
          Purchase Order
        </div>
        <div className="p-1">
          {visibleItems.map((item) => {
            const locked = isFakturSjLocked(item)
            return (
              <Menu.Item key={item.path} disabled={locked}>
                {({ active }) => (
                  <button
                    type="button"
                    disabled={locked}
                    onClick={() => !locked && handleOpen(item)}
                    title={locked ? "Cetak DO terlebih dahulu" : undefined}
                    className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left transition ${
                      locked ? "opacity-40 cursor-not-allowed" : active ? "bg-neutral-100" : "bg-white"
                    }`}
                  >
                    <span className="text-xs font-semibold text-neutral-800">{item.label}</span>
                    <span className="text-[10px] text-neutral-500">
                      {locked ? "Cetak DO dulu" : item.description}
                    </span>
                  </button>
                )}
              </Menu.Item>
            )
          })}
        </div>
      </Menu.Items>
    </Menu>
  )
}
